import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPhoneNumber,
  withTextLocalizer,
  LocalizePropType,
  forgetPassword,
  verifyPhone,
  setNotification,
  APP,
} from 'getaway-data-layer';
import {
  attachInputListeners,
  navigateTo,
  removeInputListeners,
} from '../../../utils';
import VerifyPhoneView from './VerifyPhoneView';

type Props = {
  localize: LocalizePropType;
};

const RESEND_COUNTDOWN = 15;

const VerifyPhoneContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const storedPhoneNumber: any = useSelector(selectPhoneNumber);
  const [resendCountdown, setResendCountdown] = useState(
    RESEND_COUNTDOWN
  ) as any;
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [phoneCode, setPhoneCode] = useState<string | null>(null);

  const onSuccess = (res) => {
    if (res?.email && res?.token) {
      const encodedEmail = encodeURIComponent(res.email);
      const encodedToken = encodeURIComponent(res.token);

      navigateTo(
        history,
        `/reset-password?email=${encodedEmail}&token=${encodedToken}`
      );
    }
  };

  const turnstileCheck = () => {
    // If the widget already exists, just reset it to generate a new token.
    if (turnstileWidgetId) {
      window.turnstile.reset(turnstileWidgetId);
      return;
    }

    const widgetId = window.turnstile.render(turnstileRef.current, {
      sitekey: process.env.VITE_TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      theme: 'light',
      retry: 'never',
    });

    setTurnstileWidgetId(widgetId);
  };

  const onSubmit = (code: string) => {
    setPhoneCode(code);
    if (
      process.env.VITE_TURNSTILE_SITE_KEY &&
      window.turnstile &&
      window.location.hostname !== 'localhost'
    ) {
      turnstileCheck();
    } else {
      dispatch(
        setNotification({
          message: 'backend.error',
          type: APP.NOTIFICATION_TYPE.ERROR,
        })
      );
    }
  };

  const onResendPressed = () => {
    dispatch(
      forgetPassword(null, storedPhoneNumber, turnstileToken, localize, () => {
        setResendCountdown(RESEND_COUNTDOWN);
      })
    );
  };

  const onClose = () => {
    history.goBack();
  };

  const onPressPhone = () => {
    history.goBack();
  };

  useEffect(() => {
    attachInputListeners();
    const resendCountdownUpdaterId = setInterval(() => {
      setResendCountdown((prevCountdown) => {
        return prevCountdown - 1;
      });
    }, 1000);

    setTimeout(() => {
      const inputElement = document?.querySelector(
        'input[name="one-time-code"]'
      );
      // @ts-ignore
      inputElement && inputElement?.click();
    }, 300);

    return () => {
      if (resendCountdownUpdaterId) {
        clearInterval(resendCountdownUpdaterId);
      }
      removeInputListeners();
    };
  }, []);

  useEffect(() => {
    if (turnstileToken && phoneCode) {
      dispatch(
        verifyPhone(
          storedPhoneNumber,
          phoneCode,
          turnstileToken,
          (res) => {
            onSuccess(res);
          },
          () => {
            setTurnstileToken(null);
          }
        )
      );
    }
  }, [turnstileToken, phoneCode]);

  return (
    <VerifyPhoneView
      localize={localize}
      phoneNumber={storedPhoneNumber}
      resendCountdown={resendCountdown}
      onClose={onClose}
      onSubmit={onSubmit}
      onResendPressed={onResendPressed}
      onPressPhone={onPressPhone}
      turnstileRef={turnstileRef}
    />
  );
};

export default withTextLocalizer(VerifyPhoneContainer);
